<template>
    <div v-if="paymentBalance > 0">
        <div class="d-flex flex-column flex-lg-row justify-content-between mb-2">
            <h3 class="card-title mb-1 mb-lg-0">Payment Method</h3>
            <div v-if="for_user" class="d-flex align-items-start">
                <div class="form-check mr-3">
                    <input type="checkbox" name="accept_partial" class="form-check-input" id="accept_partial"
                        v-model="form.accept_partial" @change="setPartial()" />
                    <label for="accept_partial" class="mb-0"> Accept Partial Payment</label>
                </div>
                <AmountInput v-if="form.accept_partial" v-model="form.partial_amount" class="input-group-sm"
                    id="accept-partial-payment" />
            </div>
        </div>
        <ul class="nav nav-tabs d-flex justify-content-around">
            <template v-for="paymentIntent in payment_intents" :key="paymentIntent">
                <li v-if="!form.accept_partial || paymentIntent != 'Account Balance'" class="nav-item card-nav-item"
                    @click.prevent="setPaymentIntent(paymentIntent)">
                    <a class="px-2" :class="form.payment_intent == paymentIntent ? 'nav-link active' : 'nav-link'"
                        href="#">{{ paymentIntent }} </a>
                </li>
            </template>
        </ul>
        <card class="border-top-0">
            <div class="card-payment" v-if="form.payment_intent == 'Credit Card'">
                <SelectCard :cards="cards" :form="form" @setPaymentMethod="setPaymentMethod" />
            </div>
            <div class="bank-payment" v-if="form.payment_intent == 'ACH'">
                <SelectAccount :banks="banks" :link_token="link_token" :form="form" :bankRoute="bankRoute"
                    :canAdd="!for_user" :form_bank="formBank" @setPaymentMethod="setPaymentMethod" @bankAdded="bankAdded" />

                <p v-if="for_user" class="text-size-small text-dark">Customer must add bank account method themselves</p>
            </div>

            <div class="cash-payment" v-if="form.payment_intent == 'Cash' || form.payment_intent == 'Check'">
                <div>
                    <div v-if="$page.props.auth.user.drawer">
                        <div class="form-horizontal">
                            <div class="form-group row">
                                <label class="col-sm-4 col-form-label">Due:</label>

                                <div class="col-sm-8">
                                    <div class="input-group">
                                        <div class="input-group-prepend">
                                            <span class="input-group-text">$</span>
                                        </div>
                                        <input type="text" :value="formatCurrency(paymentAmount)" class="form-control"
                                            disabled>
                                    </div>
                                </div>
                            </div>

                            <div v-if="form.payment_intent == 'Cash'">
                                <div class="form-group row">
                                    <label class="col-sm-4 col-form-label">Tendered:</label>

                                    <div class="col-sm-8">
                                        <AmountInput v-model="form.cash_credit_amount" @input="calcCashChange" />
                                    </div>
                                </div>

                                <div v-if="form.cash_debit_amount" class="form-group row">
                                    <label class="col-sm-4 col-form-label">Change:</label>

                                    <div class="col-sm-8">
                                        <div class="input-group">
                                            <div class="input-group-prepend">
                                                <span class="input-group-text">$</span>
                                            </div>
                                            <input type="text" :value="formatCurrency(form.cash_debit_amount)"
                                                class="form-control" disabled>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="form-group row" v-if="form.payment_intent == 'Check'">
                                <label class="col-sm-4 col-form-label">Check Number:</label>

                                <div class="col-sm-8">
                                    <input type="text" v-model="form.check_number" class="form-control">
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else>
                        <p class="alert alert-warning text-dark">
                            Would you like to open a cash drawer? Cash drawers are necessary when accepting cash or checks.
                            Enter a
                            starting balance and click Open Cash Drawer before accepting cash or check payments.
                        </p>

                        <label for="starting-balance">Starting Balance:</label>
                        <AmountInput v-model="formCash.amount" inputId="starting-balance" class="mb-3" />

                        <jet-add-button @click="cashOpen">Open Cash Drawer</jet-add-button>
                    </div>
                </div>
            </div>

            <div class="month-payment" v-if="this.form.payment_intent == 'Account Balance'">
                <p v-if="mtd_instant_payment_only == 1">
                    <jet-button @click="requestAccountBalancePayment"
                        :disable="formRequestAccountBalancePayment.processing">Apply For
                        Credit</jet-button>
                </p>
                <p v-else-if="isMTD && !for_user">Will be billed to your account when items ship.</p>
                <div class="row mb-2">
                    <div class="col-sm-6">
                        <strong>Current Account Balance</strong>
                    </div>
                    <div class="col-sm-6">
                        {{ formatDollars(current_account_balance) }}
                    </div>
                </div>
                <div class="row" v-if="for_user">
                    <div class="col-sm-6">
                        <strong>Minimum Payment</strong>
                    </div>
                    <div class="col-sm-6 d-flex align-items-center">
                        <AmountInput v-model="formMinimum.payment_minimum" class="input-group-sm"
                            @keyup.enter="updateMinimum" @change="updateMinimum" />
                        <span class="ml-2 flex-center" v-if="saved_minimum"><i
                                class="fa fa-check-circle text-success"></i></span>
                    </div>
                </div>

                <div class="row" v-if="decline_reason">
                    <div class="col-sm-6">
                        <strong>Declined</strong>
                    </div>
                    <div class="col-sm-6">
                        Message from MTD: {{ decline_reason }}
                    </div>
                </div>
            </div>
        </card>
    </div>
    <h3 v-else>No Payment Required</h3>
</template>

<script>
import SelectCard from '@/Components/Payment/SelectCard'
import SelectAccount from '@/Components/Payment/SelectAccount'
import JetAddButton from '@/Jetstream/AddButton'
import AmountInput from '@/Shared/AmountInput'

export default {
    components: {
        SelectCard,
        SelectAccount,
        JetAddButton,
        AmountInput
    },
    props: {
        mtd_instant_payment_only: Number,
        payment_intents: Array,
        paymentBalance: Number,
        current_account_balance: Number,
        form: Object,
        cards: Array,
        formBank: Object,
        banks: Array,
        link_token: String,
        bankRoute: String,
        for_user: Object,
        decline_reason: String,
    },
    data() {
        return {
            formCash: this.$inertia.form({
                amount: null
            }),
            saved_minimum: false,
            formMinimum: this.$inertia.form({
                payment_minimum: this.for_user ? this.for_user.payment_minimum : null
            }),
            formRequestAccountBalancePayment: this.$inertia.form({
            }),
        }
    },
    computed: {
        paymentAmount() {
            if (this.form.accept_partial && this.form.partial_amount > 0) {
                return this.form.partial_amount;
            }

            return this.form.payment_amount;
        },
        isMTD() {
            return this.$page.props.auth.user.role == 'mtd'
        },
    },
    methods: {
        requestAccountBalancePayment() {
            this.formRequestAccountBalancePayment.post(route('mtd.requestOpenPaymentMethod'))
        },
        setPaymentIntent(paymentIntent) {
            this.form.payment_intent = paymentIntent;
            this.form.stripe_payment_method = null;
            this.$emit('setPaymentIntent', paymentIntent)
        },
        setPaymentMethod(e, id) {
            if (e.target.checked) this.form.stripe_payment_method = id;
            this.$emit('setPaymentMethod')
        },
        bankAdded() {
            this.$emit('bankAdded')
        },
        cashOpen() {
            this.formCash.post(this.route('mtd.cash.open'));
        },
        calcCashChange() {
            let amount = 0;
            if (this.form.accept_partial && this.form.partial_amount > 0) {
                amount = this.form.partial_amount;
            } else {
                amount = this.order_balance_total;
            }
            if (parseFloat(this.form.cash_credit_amount) >= parseFloat(amount)) {
                this.form.cash_debit_amount = this.form.cash_credit_amount - amount;
            } else {
                this.form.cash_debit_amount = null;
            }
        },
        setPartial() {
            if (this.form.payment_intent == 'Account Balance') {
                this.form.payment_intent = 'Credit Card'
            }
        },
        updateMinimum() {
            this.formMinimum.post(this.route('user.saveMinimumPayment', this.for_user), {
                preserveScroll: true
            })
            this.saved_minimum = true
        },
    }
}
</script>

<style>
.card-nav-item {
    flex: 1;
    text-align: center;
}

#accept-partial-payment {
    max-width: 140px;
}
</style>
